import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Shield, AlertOctagon, Clock, CheckCircle } from "lucide-react";
import ContactBlock from "../../components/contact/ContactBlock";
import { Link } from "react-router-dom";

const WarrantyScreen = () => {
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const sections = [
		{
			icon: <Shield className="w-8 h-8 text-primary" />,
			title: "5-Year Warranty",
			content: [
				"All of our products are made locally in Toronto by expert craftsmen using top grade materials and components. Under normal use, your products should operate problem free for years.",
				"In the rare event that you experience issues related to manufacturing defects, contact our customer service team, and we will repair or replace the defective product.",
				"The Warranty is extended to the original purchaser only, is non-transferable, and only applies to custom-made products in the original windows for which the product was ordered and intended.",
				"Valid for five years from the date of purchase.",
			],
		},
		{
			icon: <AlertOctagon className="w-8 h-8 text-primary" />,
			title: "Warranty Exceptions and Exemptions",
			content: [
				"The warranty does not cover defects resulting from abuse, misuse, normal wear and tear, accidents, alterations, exposure to the elements, excessive humidity, or failure to follow use and care instructions.",
				"Normal maintenance associated with motors and related accessories including, but not limited to, recharging batteries.",
				"Any condition or damage resulting from removal of the product or reinstallation in the same or another window or application.",
				"Products unusable by reason of miss-measurement or assembly or installation not in accordance with product installation instructions.",
				"Shipping costs, costs of removal, re-measuring and reinstallation are the responsibility of the purchaser.",
			],
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>
					Warranty: 5-Year Warranty On Window Shades | Off Cut Shades
				</title>
				<meta
					name="description"
					content="Off Cut Shades warranty policy. Our custom window shades come with a 5-year warranty with professional and reliable customer support."
				/>
				<link rel="canonical" href="https://offcutshades.com/help/warranty" />
			</Helmet>

			<main className="container mx-auto px-4 py-12">
				{/* Hero Section */}
				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<motion.div
						className="flex justify-center mb-6"
						initial={{ scale: 0 }}
						animate={{ scale: 1 }}
						transition={{ ...bounceTransition, delay: 0.2 }}
					>
						<img
							src="/images/newHome/Warranty-policy-80x80.png"
							alt="Warranty Policy"
							className="w-20 h-20"
						/>
					</motion.div>

					<motion.h1
						className="relative z-10 text-5xl md:text-7xl font-bold mb-6 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.3 }}
					>
						Our{" "}
						<span className="relative">
							<span className="text-primary">Warranty</span>
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-600 max-w-3xl mx-auto"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.4 }}
					>
						We are pleased to offer the peace of mind that, after you buy a
						product from us, we will be there in case something unexpected
						happens.
					</motion.p>
				</motion.div>

				{/* Warranty Features */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
					{[
						{
							icon: <Clock className="w-10 h-10 text-primary" />,
							title: "5 Years",
							description: "Extended warranty coverage",
						},
						{
							icon: <Shield className="w-10 h-10 text-primary" />,
							title: "Protection",
							description: "Against manufacturing defects",
						},
						{
							icon: <CheckCircle className="w-10 h-10 text-primary" />,
							title: "Support",
							description: "When you need it",
						},
					].map((feature, index) => (
						<motion.div
							key={index}
							className="bg-white rounded-2xl p-6 text-center shadow-lg"
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
						>
							<div className="flex justify-center mb-4">{feature.icon}</div>
							<h3 className="text-xl font-bold mb-2">{feature.title}</h3>
							<p className="text-gray-600">{feature.description}</p>
						</motion.div>
					))}
				</div>

				{/* Warranty Details */}
				<div className="space-y-8">
					{sections.map((section, index) => (
						<motion.div
							key={index}
							className="bg-white rounded-3xl shadow-lg overflow-hidden"
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
						>
							<div className="p-8">
								<div className="flex items-center gap-4 mb-6">
									{section.icon}
									<h2 className="text-2xl font-bold text-gray-900">
										{section.title}
									</h2>
								</div>
								<div className="space-y-4">
									{section.content.map((paragraph, pIndex) => (
										<p key={pIndex} className="text-gray-600">
											{paragraph}
										</p>
									))}
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</main>

			{/* Contact Support Section */}
			<ContactBlock />
		</div>
	);
};

export default WarrantyScreen;
